import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import { InputVariant } from "@galaxy-io/dls/inputs/Input";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Box, { BoxVariant } from "@galaxy-io/dls/layout/Box";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import InfiniteTable, { InfiniteTableSize } from "@galaxy-io/dls/table/InfiniteTable";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import Widget from "@galaxy-io/dls/widget/Widget";
import {
  type ExclusionInput,
  type ExclusionRule,
  exclusionFields,
  exclusionInputSchema,
  exclusionKindLabels,
  exclusionOperators,
  type exclusionPreviewSchema,
  isSignalDateField,
} from "@gtm/contracts/core/exclusions";
import { reviewLabels, reviewStates } from "@gtm/contracts/core/review";
import { styled } from "@linaria/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { useRouter, useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { DrawerList, DrawerStack } from "@/components/workspace/drawer-content";
import { SelectField } from "@/components/workspace/select-field";
import { Status } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { date } from "@/lib/utils";

/**
 * The preview card. A hairline-divided widget: headline counts, a breakdown by
 * record type, and the sample rows the API returns alongside the totals.
 */
const PreviewStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-top: 0.5px solid ${t.color.border.primary};
  > div {
    display: flex;
    flex-direction: column;
    gap: ${t.space[2]};
    padding: ${t.space[12]};
    min-width: 0;
  }
  > div + div {
    border-left: 0.5px solid ${t.color.border.primary};
  }
`;
const PreviewHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[2]};
  padding: ${t.space[12]};
  min-width: 0;
`;
const PreviewTable = styled.div`
  min-width: 0;
  border-top: 0.5px solid ${t.color.border.primary};
`;
const PreviewNote = styled.div`
  padding: ${t.space[12]};
  min-width: 0;
  border-top: 0.5px solid ${t.color.border.primary};
`;

/** "1 person · 2 companies · 9 signals", dropping accounts when a rule has none. */
export function countLabel(rule: {
  people: number;
  companies: number;
  activities: number;
  identities: number;
}) {
  const count = (n: number, one: string, many: string) =>
    `${n.toLocaleString()} ${n === 1 ? one : many}`;
  return [
    count(rule.people, "person", "people"),
    count(rule.companies, "company", "companies"),
    count(rule.activities, "signal", "signals"),
    ...(rule.identities ? [count(rule.identities, "account", "accounts")] : []),
  ].join(" · ");
}
export const emptyRule: ExclusionInput = {
  name: "",
  enabled: true,
  field: "username",
  operator: "contains",
  value: "",
  provider: null,
  personId: null,
  companyId: null,
  identityId: null,
};
export function useExclusionActions() {
  const client = useQueryClient(),
    router = useRouter(),
    { toast } = useToast();
  const [busy, setBusy] = useState(false);
  async function refresh() {
    await client.invalidateQueries();
    window.dispatchEvent(new Event("records:changed"));
    router.refresh();
  }
  function error(error: unknown) {
    toast({
      variant: ToastVariant.ERROR,
      header: "Could not update exclusions",
      description:
        error instanceof z.ZodError
          ? error.issues[0]?.message
          : error instanceof Error
            ? error.message
            : "Please try again.",
      ...toastDefaults(ToastVariant.ERROR),
    });
  }
  async function change(
    action: () => Promise<unknown>,
    header: string,
    subheader: string,
    propagate = false,
  ) {
    setBusy(true);
    try {
      await action();
      await refresh();
      toast({
        variant: ToastVariant.SUCCESS,
        header,
        description: subheader,
        ...toastDefaults(ToastVariant.SUCCESS),
      });
      return true;
    } catch (e) {
      if (propagate) throw e;
      error(e);
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function save(raw: ExclusionInput, id?: string) {
    setBusy(true);
    try {
      const input = exclusionInputSchema.parse(raw);
      const result = await unwrap(
        id
          ? apiClient().PATCH("/exclusions/rules/{id}", {
              params: { path: { id } },
              body: input,
            })
          : apiClient().POST("/exclusions/rules", { body: input }),
      );
      await refresh();
      toast({
        variant: ToastVariant.SUCCESS,
        header: id ? "Exclusion rule updated" : "Exclusion rule created",
        description: "Matching records are hidden while this rule is enabled.",
        ...toastDefaults(ToastVariant.SUCCESS),

        action: !id
          ? {
              label: "Undo",
              onClick: () =>
                void change(
                  () =>
                    unwrap(
                      apiClient().PATCH("/exclusions/rules/{id}", {
                        params: { path: result },
                        body: { enabled: false },
                      }),
                    ),
                  "Rule paused",
                  "Other matching rules still apply.",
                ),
            }
          : undefined,
      });
      return result;
    } catch (e) {
      error(e);
      return null;
    } finally {
      setBusy(false);
    }
  }
  return { busy, save, change, error };
}
export function RuleForm({
  open,
  initial = emptyRule,
  rule,
  onClose,
  onRemoved = onClose,
}: {
  open: boolean;
  onRemoved?: () => void;
  initial?: ExclusionInput;
  /** The saved rule being edited. Absent while composing a new one. */
  rule?: ExclusionRule;
  onClose: () => void;
}) {
  const id = rule?.id;
  const router = useRouter();
  const [input, setInput] = useState(initial);
  const [preview, setPreview] = useState<z.infer<typeof exclusionPreviewSchema> | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const actions = useExclusionActions();
  const busy = actions.busy || previewing;
  const date = isSignalDateField(input.field);
  const status = input.field === "person_status";
  const [ageAmount = "3", ageUnit = "months"] = (input.value || "3 months").split(" ");
  const exact = !!(input.personId || input.companyId || input.identityId);
  const domain = [
    "email_domain",
    "website_host",
    "company_domain",
    "company_website_host",
  ].includes(input.field || "");
  function patch(next: Partial<ExclusionInput>) {
    setInput((v) => ({ ...v, ...next }));
    setPreview(null);
  }
  useDialogReset(open, () => {
    setInput(initial);
    setPreview(null);
    setPreviewing(false);
    setConfirmRemove(false);
  });
  async function previewMatches() {
    setPreviewing(true);
    try {
      setPreview(
        await unwrap(
          apiClient().POST("/exclusions/preview", {
            body: exclusionInputSchema.parse(input),
          }),
        ),
      );
    } catch (e) {
      actions.error(e);
    } finally {
      setPreviewing(false);
    }
  }
  // Pausing and removal act on the saved rule immediately, so the local copy
  // follows the server rather than leaving the form falsely dirty.
  async function setEnabled(enabled: boolean) {
    if (!rule) return;
    const changed = await actions.change(
      () =>
        unwrap(
          apiClient().PATCH("/exclusions/rules/{id}", {
            params: { path: { id: rule.id } },
            body: { enabled },
          }),
        ),
      enabled ? "Rule resumed" : "Rule paused",
      enabled ? "Matching records are hidden again." : "Other matching rules still apply.",
    );
    if (changed) setInput((v) => ({ ...v, enabled }));
  }
  const [confirmRemove, setConfirmRemove] = useState(false);
  async function remove() {
    if (!rule) return;
    const removed = await actions.change(
      () =>
        unwrap(
          apiClient().DELETE("/exclusions/rules/{id}", {
            params: {
              path: { id: rule.id },
              query: { updatedAt: rule.updatedAt },
            },
          }),
        ),
      "Rule removed",
      "Other matching rules still apply.",
      true,
    );
    if (removed) onRemoved();
  }
  return (
    <>
      <ConfirmAction
        isOpen={confirmRemove}
        onOpenChange={setConfirmRemove}
        header="Remove exclusion rule?"
        description="This rule will stop excluding records. Other matching rules still apply."
        label="Remove rule"
        isDestructive
        onConfirm={remove}
      />
      <SurfaceDialog
        open={open}
        flush
        title={id ? "Edit exclusion rule" : "New exclusion rule"}
        onClose={onClose}
        busy={busy}
        headerActions={
          rule ? (
            <>
              <Button
                size={ButtonSize.SMALL}
                label={input.enabled ? "Pause" : "Resume"}
                variant={ButtonVariant.SECONDARY}
                isDisabled={busy}
                onClick={() => void setEnabled(!input.enabled)}
              />
              <Button
                size={ButtonSize.SMALL}
                label="Remove"
                variant={ButtonVariant.SECONDARY}
                isDisabled={busy}
                onClick={() => setConfirmRemove(true)}
              />
            </>
          ) : undefined
        }
        dirty={JSON.stringify(input) !== JSON.stringify(initial)}
        footer={
          <>
            <Button
              label="Cancel"
              variant={ButtonVariant.SECONDARY}
              isDisabled={busy}
              onClick={onClose}
            />
            <Button
              label="Preview matches"
              variant={ButtonVariant.SECONDARY}
              isLoading={previewing}
              isDisabled={busy}
              onClick={() => void previewMatches()}
            />
            <Button
              label={id ? "Save rule" : "Create rule"}
              isLoading={actions.busy}
              isDisabled={busy || !preview}
              onClick={() =>
                void actions.save(input, id).then((result) => {
                  if (result) onClose();
                })
              }
            />
          </>
        }
      >
        <DrawerStack>
          {rule && (
            <DrawerList>
              <DescriptionList
                items={[
                  {
                    label: "State",
                    value: (
                      <>
                        <Status
                          value={input.enabled ? "active" : "paused"}
                          label={input.enabled ? "Enabled" : "Paused"}
                        />
                      </>
                    ),
                  },
                  {
                    label: "Excluded records",
                    value: (
                      <>
                        <Button
                          label="View excluded records"
                          variant={ButtonVariant.TERTIARY}
                          onClick={() => {
                            onClose();
                            router.push(`/exclusions?view=records&ruleId=${rule.id}`);
                          }}
                        />
                      </>
                    ),
                  },
                ]}
              />
            </DrawerList>
          )}
          <TextInput
            label="Rule name"
            ref={(input) => input?.setAttribute("aria-label", "Rule name")}
            value={input.name}
            onChange={(value) => patch({ name: value })}
            fillWidth
            variant={InputVariant.BASE}
            isDisabled={busy}
          />
          {exact ? (
            <Text>
              {input.companyId
                ? "Excludes this company, its associated people, and their signals."
                : input.personId
                  ? "Excludes this person and their signals, even if their profile changes."
                  : "Excludes this provider account and its signals."}
            </Text>
          ) : (
            <>
              <SelectField
                label="Field"
                value={input.field || "username"}
                disabled={busy}
                options={Object.entries(exclusionFields).map(([value, label]) => ({
                  value,
                  label,
                }))}
                onChange={(field) =>
                  patch({
                    field: field as ExclusionInput["field"],
                    operator: isSignalDateField(field) ? "older" : "equals",
                    value: isSignalDateField(field)
                      ? "3 months"
                      : field === "person_status"
                        ? "processed"
                        : "",
                    provider: null,
                  })
                }
              />
              <SelectField
                label="Match"
                value={input.operator || "equals"}
                disabled={busy}
                options={Object.entries(exclusionOperators)
                  .filter(([value]) =>
                    status
                      ? value === "equals"
                      : date
                        ? value === "older"
                        : value !== "older" && (domain ? value !== "contains" : value !== "domain"),
                  )
                  .map(([value, label]) => ({ value, label }))}
                onChange={(operator) => patch({ operator: operator as ExclusionInput["operator"] })}
              />
              {status ? (
                <SelectField
                  label="Person status"
                  value={input.value || "processed"}
                  disabled={busy}
                  options={reviewStates.map((value) => ({ value, label: reviewLabels[value] }))}
                  onChange={(value) => patch({ value })}
                />
              ) : date ? (
                <>
                  <TextInput
                    label="Age"
                    ref={(input) => input?.setAttribute("aria-label", "Age")}
                    value={ageAmount}
                    onChange={(value) => patch({ value: `${value} ${ageUnit}` })}
                    variant={InputVariant.BASE}
                    isDisabled={busy}
                    fillWidth
                  />
                  <SelectField
                    label="Unit"
                    value={ageUnit}
                    disabled={busy}
                    options={["days", "weeks", "months", "years"].map((value) => ({
                      value,
                      label: value,
                    }))}
                    onChange={(unit) => patch({ value: `${ageAmount} ${unit}` })}
                  />
                </>
              ) : (
                <TextInput
                  label={domain ? "Domain" : "Value"}
                  ref={(input) => input?.setAttribute("aria-label", domain ? "Domain" : "Value")}
                  value={input.value || ""}
                  onChange={(value) => patch({ value })}
                  fillWidth
                  variant={InputVariant.BASE}
                  isDisabled={busy}
                />
              )}
              {input.field === "username" && (
                <SelectField
                  label="Provider"
                  value={input.provider || ""}
                  disabled={busy}
                  options={[
                    { value: "", label: "All providers" },
                    { value: "github", label: "GitHub" },
                    { value: "reddit", label: "Reddit" },
                  ]}
                  onChange={(provider) => patch({ provider: provider || null })}
                />
              )}
              <Text>
                {status
                  ? "Hides people with this status and their signals. Matches update when a person’s status changes."
                  : date
                    ? "Hides only signals older than this rolling cutoff. Unknown dates do not match. People and their other signals remain available. Any matching exclusion rule applies."
                    : "Matches are case-insensitive. Any matching rule excludes a record. Company rules also exclude associated people and signals."}
              </Text>
              {/*
              Saving is deliberately gated on previewing — a rule hides records,
              and seeing which ones first is the point. The gate was silent
              though: the button simply sat greyed out with nothing to say why.
            */}
              {!preview && (
                <Text>Preview the matches to see what this hides before you create it.</Text>
              )}
            </>
          )}
          {preview && (
            <div aria-label="Exclusion preview" role="region">
              <Widget isFlush>
                <PreviewHeader>
                  <Text size={TextSize.HEADING_SM} weight={TextWeight.MEDIUM}>
                    {preview.total.toLocaleString()} matching records
                  </Text>
                  <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                    {preview.newlyExcluded.toLocaleString()} newly excluded
                  </Text>
                </PreviewHeader>
                <PreviewStats>
                  {(
                    [
                      ["People", preview.people],
                      ["Companies", preview.companies],
                      ["Signals", preview.activities],
                      ["Accounts", preview.identities],
                    ] as const
                  ).map(([label, count]) => (
                    <div key={label}>
                      <Text size={TextSize.BODY_LG} weight={TextWeight.MEDIUM}>
                        {count.toLocaleString()}
                      </Text>
                      <Text size={TextSize.CAPTION} variant={TextVariant.SECONDARY}>
                        {label}
                      </Text>
                    </div>
                  ))}
                </PreviewStats>
                {preview.items.length > 0 && (
                  <PreviewTable>
                    <Box variant={BoxVariant.BASE}>
                      <InfiniteTable
                        ariaLabel="Preview matches"
                        size={InfiniteTableSize.SMALL}
                        data={preview.items}
                        getRowId={(row) => `${row.kind}:${row.id}`}
                        columns={[
                          {
                            id: "name",
                            header: "Record",
                            accessor: (row) => row.name,

                            cell: ({ row }) => (
                              <Text size={TextSize.BODY_SM} lineClamp={1}>
                                {row.name}
                              </Text>
                            ),
                            isRowHeader: true,
                          },
                          {
                            id: "kind",
                            header: "Type",
                            width: 96,
                            accessor: (row) => row.kind,

                            cell: ({ row }) => (
                              <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                                {exclusionKindLabels[row.kind]}
                              </Text>
                            ),
                          },
                          {
                            id: "context",
                            header: "Company / provider",
                            width: 170,
                            accessor: (row) => row.context,

                            cell: ({ row }) => (
                              <Text
                                size={TextSize.BODY_SM}
                                variant={TextVariant.SECONDARY}
                                lineClamp={1}
                              >
                                {row.context || "—"}
                              </Text>
                            ),
                          },
                        ]}
                      />
                    </Box>
                  </PreviewTable>
                )}
                <PreviewNote>
                  <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                    {preview.total > preview.items.length
                      ? `Showing the first ${preview.items.length}. `
                      : ""}
                    Preview reflects current data. Future matches are excluded automatically.
                    {!input.enabled ? " This rule is paused; save it, then resume to apply." : ""}
                  </Text>
                </PreviewNote>
              </Widget>
            </div>
          )}
        </DrawerStack>
      </SurfaceDialog>
    </>
  );
}
export function ExclusionEditor() {
  const { params, update } = useUrlState();
  const rawId = params.get("exclusion");
  const id = useRetainedValue(rawId);
  const query = useQuery({
    queryKey: ["exclusion-rule", id],
    enabled: !!rawId && id !== "new",
    // Removing the rule leaves this query open until the drawer closes; retrying
    // the resulting 404 would hold the drawer's controls busy for seconds.
    retry: false,
    queryFn: () =>
      unwrap(
        apiClient().GET("/exclusions/rules/{id}", {
          params: { path: { id: id! } },
        }),
      ),
  });
  const close = () => update({ exclusion: null, exclusionEdit: null });
  if (!id) return null;
  if (id === "new") return <RuleForm open={rawId === "new"} key="new" onClose={close} />;
  if (!query.data)
    return (
      <SurfaceDialog
        open={!!rawId}
        drawer={params.get("exclusionEdit") !== "1"}
        title="Exclusion rule"
        onClose={close}
      >
        <QueryState
          error={query.error}
          loadingMessage="Loading rule…"
          onRetry={() => void query.refetch()}
        />
      </SurfaceDialog>
    );
  const rule = query.data;
  const initial = exclusionInputSchema.parse({
    name: rule.name,
    enabled: rule.enabled,
    field: rule.field,
    operator: rule.operator,
    value: rule.value,
    provider: rule.provider,
    personId: rule.personId,
    companyId: rule.companyId,
    identityId: rule.identityId,
  });
  return (
    <>
      <SurfaceDialog
        open={!!rawId}
        drawer
        title={rule.name}
        onClose={close}
        headerActions={
          <Button
            size={ButtonSize.SMALL}
            label="Edit"
            variant={ButtonVariant.SECONDARY}
            onClick={() => update({ exclusionEdit: "1" })}
          />
        }
      >
        <DrawerList>
          <DescriptionList
            items={[
              {
                label: "State",
                value: (
                  <>
                    <Status
                      value={rule.enabled ? "active" : "paused"}
                      label={rule.enabled ? "Enabled" : "Paused"}
                    />
                  </>
                ),
              },
              {
                label: "Condition",
                value: rule.field
                  ? `${exclusionFields[rule.field as keyof typeof exclusionFields]} ${rule.operator ? exclusionOperators[rule.operator as keyof typeof exclusionOperators] : ""} “${rule.value}”`
                  : rule.companyId
                    ? "Exact company and associated records"
                    : rule.personId
                      ? "Exact person and their signals"
                      : "Exact provider account",
              },
              { label: "Provider", value: rule.provider || "All providers" },
              {
                label: "Excluded records",
                value: (
                  <>
                    <Button
                      label="View excluded records"
                      variant={ButtonVariant.TERTIARY}
                      onClick={() =>
                        update({
                          exclusion: null,
                          exclusionEdit: null,
                          view: "records",
                          ruleId: rule.id,
                        })
                      }
                    />
                  </>
                ),
              },
              { label: "Created by", value: rule.createdByName || "—" },
              { label: "Updated", value: date(rule.updatedAt, true), family: FontFamily.MONO },
            ]}
          />
        </DrawerList>
      </SurfaceDialog>
      {
        <RuleForm
          open={!!rawId && params.get("exclusionEdit") === "1"}
          key={id}
          rule={rule}
          initial={initial}
          onClose={() => update({ exclusionEdit: null })}
          onRemoved={close}
        />
      }
    </>
  );
}
